import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import debounce from "debounce-promise";
import FormSectionLayout from "../../layouts/FormSectionLayout";
import HeadingText from "../../common/HeadingText/HeadingText";
import icnminimumordervalue from "../../../assets/images/icn_minimum_order_value.svg";
import {
  Col,
  Container,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Spinner,
  Label,
  ButtonGroup,
} from "reactstrap";
import TextField from "../../common/TextField/TextField";
import AsyncSelectComponent from "../../common/AsyncSelect/AsyncSelect";
import OrderListComponent from "./OrderListComponent/OrderListComponent";
import "./AddUpdateOrderPage.scss";
import ButtonComponent from "../../common/Button/ButtonComponent";
import {
  IActivePromosForOrderPayload,
  IAddUpdateOrdersState,
  IBasketPromoDiscountSlabs,
  IDropdownButtonList,
  IOrderAddUpdateList,
  IPromoBasketDetailResponse,
  IBasketPromoMeta,
  IOrderDiscountObject,
  IOrderDetailResponseNormalized,
  IProductDetailsResponse,
  IZoneInfoState,
  IPartnerChannel,
} from "../../../interfaces/Index";
import { IAddUpdateOrderPageProps } from "../../../interfaces/order.interface";
import DropdownButton from "../../common/ButtonDropDown/ButtonDropDown";
import Modal from "../../common/Modal/Modal";
import DataTableProductSearch from "../../common/DataTableProductSearch/DataTableProductSearch";
import LoaderComponent from "../../common/Loader/Loader";
import { bodyDataAndKeyTypes, footerData } from "./OrderDataTableConstant";
import {
  _,
  formatToCurrency,
  getTomorrowDate,
  removeArrayOfObjectDuplicates,
  formatDate,
  allowedRole,
} from "../../../utils/lib";
import { decimalOnlyValidator } from "../../../utils/Validators";
import { SelectDropDown } from "../../common/SelectDropDown/SelectDropDown";
import {
  fetchCustomerApiCall,
  getStoreDetailApiCall,
} from "../../../redux/sources/CustomerSource";
import {
  fetchOdooTaxApiCall,
  fetchProductsForOrderApiCall,
} from "../../../redux/sources/GlobalProductSource";
import { normalizeOrderCustomerSearchResponse } from "../../../normalizer/CustomerNormalizer";
import { normalizeOrderProductSearchResult } from "../../../normalizer/GlobalProductNormalizer";
import {
  normalizeCheckProductPayload,
  normalizeCreateOrderPayload,
  normalizeFetchProductsForOrderPayload,
  normalizeUpdateOrderPayload,
} from "../../../normalizer/OrderNormalizer";
import { AbilityContext } from "../../../config/Can";
import {
  checkProductAvailabilityAPI,
  fetchMinimumOrderValueApiCall,
} from "../../../redux/sources/OrderSource";
import { getBulkProductByCategoryIds } from "../../../redux/sources/CategorySource";
import { fetchUCDetailsApiCall } from "../../../redux/sources/ZoneSource";
import {
  CREDIT_USER_STATUS,
  CUSTOMER_CHANNEL,
  DATE_FORMAT,
  ERROR_CODES,
} from "../../../config/constants";
import Toaster from "../../common/Toaster/Toaster";
import DatePicker from "react-datepicker";
import CaretIcon from "assets/images/down-arrow.svg";
import { addHours } from "date-fns";
import LoggingViewer from "components/common/LoggingViewer/LoggingViewer";
import Switch from "components/common/Switch/Switch";
import Checkbox from "components/common/Checkbox/Checkbox";
import { ICreditPaymentPlansResponse } from "interfaces/credit.interface";
import { generatePDFHandler } from "./InvoiceGenerationHtmlTemplate";
import DownloadButton from "assets/images/download-button.svg";

export const getTaxList = async () => {
  return await fetchOdooTaxApiCall();
};

export const getProductCategoryIdAndUpdateOrderList = async (
  orderDetails: IOrderDetailResponseNormalized,
  setOrderState: any,
  setIsLoading: any
) => {
  const idPayload = {
    ids: orderDetails.orderList.map((list) => list.id),
  };
  const productDetails: IProductDetailsResponse[] =
    await getBulkProductByCategoryIds(idPayload);

  orderDetails.orderList.forEach((listItem) => {
    const index = productDetails.findIndex(
      (product) => product.id.toString() === listItem.id
    );
    listItem.categoryId = productDetails[index].categoryId;
    listItem.brandId = productDetails[index].brand;
  });

  setIsLoading(false);

  setOrderState((prevState: any) => ({
    ...prevState,
    orderNumber: orderDetails.orderNumber,
    customerId: orderDetails.customerId,
    customerNumber: orderDetails.customerNumber,
    customerStore: orderDetails.customerStore,
    customerAddress: orderDetails.customerAddress,
    orderDiscount: orderDetails.orderDiscount,
    orderList: orderDetails.orderList.slice(),
    zoneId: orderDetails.zoneId,
  }));
};

export const doAPIsearch = async (val: any) => {
  if (val.length >= 3) {
    return normalizeOrderCustomerSearchResponse(
      await fetchCustomerApiCall({
        searchKey: val,
        status: "ACTIVE",
        size: "5",
      })
    );
  } else {
    return new Promise((resolve) => {
      resolve([]);
    });
  }
};

export const doProductAPIsearch = async (
  val: any,
  setProductData: any,
  setProductLoading: any,
  orderListProducts: any,
  selectedProducts: any,
  zoneInfoRef: IZoneInfoState,
  customerChannel: string,
  partnerChannels: IPartnerChannel[]
) => {
  setProductLoading(true);
  const paginationDetails = {
    searchKey: val,
    size: "25",
    sortColumn: "title",
    sortDirection: "ASC",
  };
  const productData: any = await fetchProductsForOrderApiCall(
    normalizeFetchProductsForOrderPayload(
      paginationDetails,
      zoneInfoRef,
      customerChannel,
      partnerChannels
    )
  );

  if (productData && productData.productData) {
    let normalizedProducts: Array<IOrderAddUpdateList> =
      normalizeOrderProductSearchResult(productData.productData);
    if (selectedProducts.length > 0) {
      normalizedProducts = removeArrayOfObjectDuplicates(
        normalizedProducts,
        selectedProducts,
        "id"
      );
    }
    if (orderListProducts.length > 0) {
      normalizedProducts = removeArrayOfObjectDuplicates(
        normalizedProducts,
        orderListProducts,
        "id"
      );
    }
    setProductData(normalizedProducts);
    setProductLoading(false);
  }
};

export const callStatusChange = (
  id: string,
  updateOrderStatus: any,
  status: string
) => {
  let confirmMsg = window.confirm(
    `Are you sure you want to mark order as ${status}`
  );
  if (confirmMsg) {
    updateOrderStatus(id, {
      status: status,
    });
  }
};

export const checkAndApplyBasketPromoDiscount = (
  orderList: IOrderAddUpdateList[],
  promos: IActivePromosForOrderPayload,
  showOrderDiscountModal: IOrderDiscountObject,
  applyDiscount: any
) => {
  let total = 0;
  const amountNeededForDiscount = Number(
    (promos.BASKET[0].discountSlab[0] as IBasketPromoDiscountSlabs).amount
  );
  const discountAmount = Number(promos.BASKET[0].discountSlab[0].discount);

  orderList.forEach((listItem) => {
    if (
      (promos.BASKET[0] as IPromoBasketDetailResponse).payload
        .includedCategories[0].categoryId === listItem.categoryId
    ) {
      const amount = Number(
        (listItem.productOriginalPrice as number) * listItem.quantity
      );
      total += amount;
    }
  });
  if (total >= amountNeededForDiscount) {
    if (
      !showOrderDiscountModal.isBasketDiscount &&
      Number(showOrderDiscountModal.value) === 0
    ) {
      applyDiscount(true, {
        show: false,
        value: Number(discountAmount),
        isBasketDiscount: true,
        promoId: promos.BASKET[0].promoId,
        promoType: promos.BASKET[0].payload.type,
      });
    }
  } else {
    if (
      showOrderDiscountModal.isBasketDiscount &&
      Number(showOrderDiscountModal.value) > 0
    ) {
      applyDiscount(true, {
        show: false,
        value: 0,
        isBasketDiscount: false,
        promoId: "",
        promoType: "",
      });
    }
  }
};

const AddUpdateOrderPage: React.FunctionComponent<IAddUpdateOrderPageProps> = (
  props
) => {
  let totalOrderLevelDiscount: number = 0;
  let totalDiscount: number = 0;
  const walletAmount: number = props.orderPriceBreakdown
    ? props.orderPriceBreakdown.walletAmount
    : 0;
  const creditAmount: number = props.orderPriceBreakdown
    ? props.orderPriceBreakdown.creditAmount
    : 0;
  const cash: number = props.orderPriceBreakdown
    ? props.orderPriceBreakdown.cashAmount
    : 0;
  let subTotal: number = 0;
  let grandTotal: any = 0;
  let unFormattedGrandTotal: any = 0;
  let canUpdateWalletOrder = true;
  let selectedProducts: any = useRef([]);
  let selectedProductIndex: any = useRef([]);
  let zoneInfoRef: any = useRef<IZoneInfoState>({});
  let taxList: any = useRef([]);
  let showMinimumOrderValueModal: any = useRef(false);
  let minimumOrderValueResult: any = useRef();
  let zoneStatus = true;
  let creditPlanServiceCharge = 0;
  const { fetchCreditUser } = props;
  const doZoneIdRequestSearch = async (
    orderDetails: IOrderDetailResponseNormalized,
    zoneInfoRef: any,
    handleProductAvailability: any,
    minimumOrderValueResult: any,
    zoneStatus: boolean
  ) => {
    const storeDetails = await getStoreDetailApiCall(orderDetails.customerId);
    orderDetails.customerNTN = storeDetails.ntn;
    orderDetails.customerChannel = storeDetails.customerChannel;
    orderDetails.partnerChannels = storeDetails.partnerChannels;

    setOrderState({
      ...orderState,
      customerNTN: storeDetails.ntn,
      customerChannel: storeDetails.customerChannel,
      partnerChannels: storeDetails.partnerChannels,
    });

    await fetchAndUpdateZoneInfoRef(
      storeDetails.address.zoneId,
      zoneInfoRef,
      minimumOrderValueResult,
      zoneStatus
    );
    await handleProductAvailability(orderDetails);
  };

  const fetchAndUpdateZoneInfoRef = async (
    ucId: string,
    zoneInfoRef: any,
    minimumOrderValueResult: any,
    zoneStatus: boolean
  ) => {
    zoneInfoRef.current = await fetchUCDetailsApiCall(ucId);
    if (zoneInfoRef.current.zoneStatus === "ACTIVE") {
      minimumOrderValueResult.current = await fetchMinimumOrderValueApiCall(
        zoneInfoRef.current.cityId
      );
      zoneStatus = true;
    } else {
      zoneStatus = false;
      // alert("This customer's zone is inactive. Cannot place an order for customers in this zone");
      showToast(
        "error",
        "This customer's zone is inactive. Cannot place an order for customers in this zone"
      );
    }
    return zoneStatus;
  };

  if (
    zoneInfoRef.current.zoneStatus !== "ACTIVE" &&
    zoneInfoRef.current.zoneStatus !== undefined
  ) {
    zoneStatus = false;
  } else if (zoneInfoRef.current.zoneStatus === "ACTIVE") {
    zoneStatus = true;
  }
  const ability = useContext(AbilityContext);

  const [orderState, setOrderState] = useState<IAddUpdateOrdersState>({
    id: props.order ? props.order.id || "" : "",
    orderNumber: props.order ? props.order.orderNumber || "" : "",
    customerStore: props.order ? props.order.customerStore || "" : "",
    customerNumber: props.order ? props.order.customerNumber || "" : "",
    customerAddress: props.order ? props.order.customerAddress || "" : "",
    customerId: props.order ? props.order.customerId || "" : "",
    customerNTN: props.order ? props.order.customerNTN || "" : "",
    customerChannel: props.order ? props.order.customerChannel || "" : "",
    orderDiscount: props.order ? props.order.orderDiscount || 0 : 0,
    orderList:
      props.order && props.order.orderList
        ? props.order.orderList || []
        : props.testingOrderProp || [],
    // creditUserStatus: props.order
    //   ? props.order.creditUserStatus
    //   : CREDIT_USER_STATUS.NOT_STARTED,
  });

  const [unitsAvailable, setUnitsAvailable] = useState([undefined]);
  const [CartOldValue, setCartOldValue] = useState(orderState.customerStore);

  const [isUpdateInProgress, setIsUpdateInProgress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearch, setIsSearching] = useState(props.dummyIsSearch || false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setIsShowModal] = useState(false);
  const [showDiscountModal, setIsShowDiscountModal] = useState({
    show: false,
    value: 0,
    additionalDiscount: 0,
    selectedRow: -1,
  });
  const [showOrderDiscountModal, setIsShowOrderDiscountModal] = useState({
    show: false,
    value: 0,
    isBasketDiscount: false,
    promoId: "",
    promoType: "",
  });
  const [showToaster, setToaster] = useState({
    isVisible: false,
    type: "",
    message: "",
  });

  const showToast = (type: string, message: string) => {
    setToaster({
      isVisible: true,
      type: type,
      message: message,
    });
  };

  const [productData, setProductData] = useState<Array<IOrderAddUpdateList>>(
    props.dummyProductDataProp || []
  );
  const [isProductLoading, setProductLoading] = useState(false);
  const [notificationModal, setNotificationModal] = useState(false);
  const [updateOrder, setUpdateOrder] = useState(false);
  const [createOrder, setCreateOrder] = useState(false);
  const [bundleProductExist, setBundleProductExist] = useState(false);
  const [validateProductListVal, setValidateProductListVal] = useState({
    validateForm: "enabled",
  });
  const [zoneStatusInfo, setZoneStatus] = useState({ validateForm: "enabled" });
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null);
  const [orderType, setOrderType] = useState("GO"); // general order
  const [useCredit, setUseCredit] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");

  const createdAt = props.orderDetails.createdDate
    ? props.orderDetails.createdDate
    : "";
  const createdBy =
    props.bulkCustomerDetails &&
    props.bulkCustomerDetails[props.orderDetails.createdBy]
      ? props.bulkCustomerDetails[props.orderDetails.createdBy].fullName
      : "";
  const updatedAt = props.orderDetails.updatedDate
    ? props.orderDetails.updatedDate
    : "";
  const updatedBy =
    props.bulkCustomerDetails &&
    props.bulkCustomerDetails[props.orderDetails.updatedBy]
      ? props.bulkCustomerDetails[props.orderDetails.updatedBy].fullName
      : "";

  const isCorporate =
    orderState.customerChannel === CUSTOMER_CHANNEL.CORPORATE ||
    orderState.customerChannel === CUSTOMER_CHANNEL.STREET ||
    orderState.customerChannel === CUSTOMER_CHANNEL.OFFICES_INSTITUTIONS;

  const isSelfServiceStore =
    orderState.customerChannel === CUSTOMER_CHANNEL.SELF_SERVICE_STORE;

  useEffect(() => {
    const { getPromosForOrder, getOrderPriceBreakdown } = props;
    const fetchTaxList = async () => {
      taxList.current = await getTaxList();
    };
    fetchTaxList();
    getOrderPriceBreakdown({ orderNumbers: orderState.orderNumber.toString() });
    ability.can("avail", "promo") && getPromosForOrder();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const { getOrderDetails, isEdit, order } = props;
    if (isEdit && order && order.id) {
      getOrderDetails(order.id.toString());
    }
  }, []);

  useEffect(() => {
    const { orderDetails, getBulkCustomerDetailsByIdAction } = props;

    if (props.isOrderDetailsFetched && props.orderDetails) {
      var bundleExist = props.orderDetails.orderList?.some((ele) => {
        return ele.bundleId != null && ele.bundleId != "";
      });

      if (bundleExist) {
        setBundleProductExist(true);
      }

      if (orderDetails.deliveryType == "DROPSHIP") {
        setOrderType("DO");
      }

      setIsShowOrderDiscountModal({
        ...showOrderDiscountModal,
        isBasketDiscount:
          orderDetails.promos && orderDetails.promos?.length > 0 ? true : false,
        promoId:
          orderDetails.promos && orderDetails.promos?.length > 0
            ? orderDetails.promos[0].promoId
            : "",
        promoType:
          orderDetails.promos && orderDetails.promos?.length > 0
            ? orderDetails.promos[0].type
            : "",
        value:
          orderDetails.promos && orderDetails.promos?.length > 0
            ? orderDetails.promos[0].value
            : (orderDetails.orderDiscount as number),
      });
      setDeliveryDate(orderDetails.expectedDeliveryDate as any);
      setIsLoading(true);

      doZoneIdRequestSearch(
        props.orderDetails,
        zoneInfoRef,
        handleProductAvailability,
        minimumOrderValueResult,
        zoneStatus
      );

      //fetch user details for both created and updated
      const ids: string[] = [];
      if (props.orderDetails.createdBy) {
        ids.push(props.orderDetails.createdBy);
      }
      if (props.orderDetails.updatedBy) {
        ids.push(props.orderDetails.updatedBy);
      }

      if (ids.length) getBulkCustomerDetailsByIdAction(ids);

      getProductCategoryIdAndUpdateOrderList(
        props.orderDetails,
        setOrderState,
        setIsLoading
      );
    }
  }, [props.isOrderDetailsFetched]);

  useEffect(() => {
    const { error, isError } = props;
    const errorCode = error && (error! as any).errorCode;
    const minimumOrderValueStatus = ERROR_CODES.MINIMUM_ORDER_VALUE_STATUS;

    if (isError) {
      setIsUpdateInProgress(false);
      if (errorCode === minimumOrderValueStatus) {
        setNotificationModal(false);
        showMinimumOrderValueModal.current =
          !showMinimumOrderValueModal.current;
      } else {
        alert((error! as any).errors);
      }
    }
  }, [props.isError]);

  useEffect(() => {
    const {
      isOrderCreated,
      backButtonHandler,
      isOrderUpdated,
      clearOrderDetails,
      isOrderStatusUpdated,
    } = props;
    if (isOrderCreated || isOrderUpdated || isOrderStatusUpdated) {
      clearOrderDetails();
      backButtonHandler(true);
    }
  }, [props.isOrderCreated, props.isOrderUpdated, props.isOrderStatusUpdated]);

  const { isEdit, order, error } = props;

  const { customerStore, customerNumber, orderList, customerChannel } =
    orderState;

  const headingText = isEdit
    ? order
      ? `#${order.orderNumber}`
      : ""
    : "New order";

  const buttonText = isEdit ? "Update order" : "Create order";

  const isOrderEditBlocked =
    props.orderDetails &&
    typeof props.orderDetails.isLocked !== "undefined" &&
    props.orderDetails.isLocked
      ? true
      : false;

  const notificationModalState = () => {
    setNotificationModal(!notificationModal);
    setCreateOrder(false);
    setUpdateOrder(false);
  };

  const isCreateOrder = () => {
    let minOrderValue = minimumOrderValueResult.current.minOrderValue - 1;
    if (grandTotal <= minOrderValue) {
      setNotificationModal(true);
      showMinimumOrderValueModal.current = true;
    } else if (useCredit && !selectedPlan) {
      alert("Please select plan for credit");
    } else {
      setNotificationModal(true);
      notificationModalState();
      setCreateOrder(!createOrder);
    }
  };

  const isUpdateOrder = () => {
    let minOrderValue = minimumOrderValueResult.current.minOrderValue - 1;
    if (grandTotal <= minOrderValue) {
      setNotificationModal(true);
      showMinimumOrderValueModal.current = true;
    } else {
      notificationModalState();
      setUpdateOrder(!updateOrder);
    }
  };

  const handleStatusChange = (e: any) => {
    const { order, updateOrderStatus } = props;
    callStatusChange(order.id as string, updateOrderStatus, e.target.id);
  };

  const list: Array<IDropdownButtonList> = [
    {
      id: 1,
      buttonName: "Mark as invalid",
      clickHandler: handleStatusChange,
      key: "INVALID",
    },
    {
      id: 2,
      buttonName: "Cancel order",
      clickHandler: handleStatusChange,
      key: "CANCELLED",
    },
  ];

  const handleOrderListChange = (e: any, indexSelected: number) => {
    const key: string = e.target.id.split("-")[0];
    const newValue = e.target.value;
    if (key === "remove") {
      orderList.splice(indexSelected, 1);
    } else {
      orderList[indexSelected][key] =
        key === "quantity"
          ? isNaN(parseFloat(newValue))
            ? 0
            : parseFloat(newValue)
          : newValue;
    }
    // Realize => Call function to realize basket discount here remove and quantity update
    validateAndCallApplyBasketPromoMethod();

    setOrderState({ ...orderState, ...orderList });
  };

  const validateAndCallApplyBasketPromoMethod = (isUpdate: boolean = false) => {
    const ordersList = isUpdate
      ? [...orderList, ...selectedProducts.current]
      : [...orderList];
    props.promosForOrder &&
      props.promosForOrder.BASKET &&
      props.promosForOrder?.BASKET.length > 0 &&
      checkAndApplyBasketPromoDiscount(
        ordersList,
        props.promosForOrder as IActivePromosForOrderPayload,
        showOrderDiscountModal,
        applyDiscount
      );
  };

  const handleShowDiscountModal = (flag: boolean, indexSelected: number) => {
    setIsShowDiscountModal({
      show: flag,
      value: orderList[indexSelected].discount,
      additionalDiscount: orderList[indexSelected].additionalDiscount as number,
      selectedRow: indexSelected,
    });
  };

  const handleShowOrderDiscountModal = (flag: boolean) => {
    setIsShowOrderDiscountModal({
      show: flag,
      value: Math.abs(Number(orderState.orderDiscount)),
      isBasketDiscount: showOrderDiscountModal.isBasketDiscount,
      promoId: showOrderDiscountModal.promoId,
      promoType: showOrderDiscountModal.promoType,
    });
  };

  const handleDiscountChange = (e: any) => {
    if (e.target.id === "order-discount") {
      setIsShowOrderDiscountModal({
        show: showOrderDiscountModal.show,
        value: e.target.value,
        isBasketDiscount: showOrderDiscountModal.isBasketDiscount,
        promoId: showOrderDiscountModal.promoId,
        promoType: showOrderDiscountModal.promoType,
      });
    } else {
      setIsShowDiscountModal({
        show: showDiscountModal.show,
        value: showDiscountModal.value,
        additionalDiscount: e.target.value,
        selectedRow: showDiscountModal.selectedRow,
      });
    }
  };

  const applyDiscount = (
    isOrderLevelDiscount: boolean = false,
    orderDiscountObject: IOrderDiscountObject | null = null,
    isManualDiscount: boolean = false
  ) => {
    if (isOrderLevelDiscount) {
      if (isManualDiscount) {
        (orderState.orderDiscount as any) = Math.abs(
          Number(showOrderDiscountModal.value)
        );
      } else {
        (orderState.orderDiscount as any) =
          orderDiscountObject &&
          orderDiscountObject.isBasketDiscount &&
          ((orderDiscountObject?.value as number) === 0 ||
            orderDiscountObject?.value)
            ? orderDiscountObject?.value
            : 0;
      }
      setOrderState({ ...orderState });
      setIsShowOrderDiscountModal({
        show: false,
        value: isManualDiscount
          ? showOrderDiscountModal.value
          : orderDiscountObject && orderDiscountObject.isBasketDiscount
          ? orderDiscountObject.value
          : 0,
        isBasketDiscount: orderDiscountObject
          ? orderDiscountObject?.isBasketDiscount
          : false,
        promoId: orderDiscountObject ? orderDiscountObject.promoId : "",
        promoType: orderDiscountObject ? orderDiscountObject.promoType : "",
      });
    } else {
      orderList[showDiscountModal.selectedRow].additionalDiscount = Number(
        showDiscountModal.additionalDiscount
      );
      setOrderState({ ...orderState, ...orderList });
      setIsShowDiscountModal({
        show: false,
        value: 0,
        additionalDiscount: 0,
        selectedRow: -1,
      });
    }
  };

  const handleGoBackButton = () => {
    props.clearOrderDetails();
    props.clearBulkCustomerDetailsNewAction();
    props.backButtonHandler(false);
  };

  const handleSearchInputChange = debounce((val) => doAPIsearch(val), 1000);

  const debounceSearch = useCallback(
    _.debounce(
      (
        value: any,
        orderList: any,
        orderState: IAddUpdateOrdersState,
        customerChannel: string
      ) =>
        doProductAPIsearch(
          value,
          setProductData,
          setProductLoading,
          orderList,
          selectedProducts.current,
          zoneInfoRef.current,
          customerChannel,
          orderState.partnerChannels || []
        ),
      1000
    ),
    []
  );

  const handleProductSearchInputChange = (e: any) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 3) {
      debounceSearch(
        value,
        orderState.orderList.slice(),
        orderState,
        customerChannel
      );
    }
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      setProductData([]);
    }
  };

  const handleAsyncSelectChange = async (e: any, action: any) => {
    if (!isEdit && CartOldValue !== e.value) {
      setCartOldValue(e.value);
      orderState.orderList = [];

      setOrderState({ ...orderState, ...orderList });
    }

    const zoneStatusInfoComplete = fetchAndUpdateZoneInfoRef(
      e.zoneId,
      zoneInfoRef,
      minimumOrderValueResult,
      zoneStatus
    );
    if ((await zoneStatusInfoComplete) === false) {
      setZoneStatus({ validateForm: "disabled" });
    }

    props.clearCreditLimitAndPlansAction();

    setUseCredit(false);
    fetchCreditUser(e.customerId);
    setOrderState({
      ...orderState,
      customerStore: e.storeName,
      customerNumber: e.number,
      customerAddress: e.storeAddress,
      customerId: e.customerId,
      customerChannel: e.customerChannel,
      partnerChannels: e.partnerChannels || [],
    });
  };

  const handleRecordSelection = (event: any, id: any) => {
    let recordId = id;
    if (productData[recordId].isChecked) {
      // remove from selected Items
      const index = selectedProductIndex.current.indexOf(recordId);
      selectedProductIndex.current.splice(index, 1);
      selectedProducts.current.splice(index, 1);
      productData[recordId].isChecked = false;
    } else {
      selectedProductIndex.current.push(recordId);
      selectedProducts.current.push(productData[recordId]);
      productData[recordId].isChecked = true;
    }

    setProductData([...productData]);
  };

  const updateOrderList = () => {
    // Realize => Call here when products added
    validateAndCallApplyBasketPromoMethod(true);

    setOrderState({
      ...orderState,
      orderList: [...orderList, ...selectedProducts.current],
    });
    selectedProducts.current = [];
    selectedProductIndex.current = [];
    setIsShowModal(false);
    setIsSearching(false);
    setSearchTerm("");
    setProductData([]);
  };

  const closeSearch = () => {
    setIsSearching(false);
    setSearchTerm("");
    setProductData([]);
  };

  const closeModal = () => {
    selectedProducts.current = [];
    selectedProductIndex.current = [];
    showMinimumOrderValueModal.current = false;
    setNotificationModal(false);
    setCreateOrder(false);
    setProductData([]);
    setIsShowModal(false);
    setIsSearching(false);
    setSearchTerm("");
  };

  const handleProductAvailability = async (orderDetails: any) => {
    const payload = normalizeCheckProductPayload(
      orderDetails,
      zoneInfoRef.current,
      orderDetails.customerChannel
    );
    const stock = await checkProductAvailabilityAPI(payload);
    setUnitsAvailable(stock.map((unit) => unit.availableStock));
  };

  const validateStock = (
    inputQtyMap: Map<string, number>,
    availableQtyMap: Map<string, number>,
    orderQtyMap?: Map<string, number>
  ): boolean => {
    let invalidStockCount = 0;
    inputQtyMap.forEach((inputQty: number, id: string) => {
      let availableQty = availableQtyMap.get(id) || 0;
      let orderQty = orderQtyMap?.get(id) || 0;

      if (inputQty > availableQty + orderQty) {
        invalidStockCount++;

        let orderItem = orderList.find((item) => item.id === id);
        if (orderItem) orderItem.stock = availableQty;
      }
    });

    if (invalidStockCount == 0) return true; // All items have valid stock

    setOrderState({ ...orderState, ...orderList });
    setValidateProductListVal({ validateForm: "disabled" });
    notificationModalState();
    closeModal();
    showToast(
      "warning",
      "Some items are not available in the requested quantities. Please edit your order accordingly."
    );
    return false;
  };

  const handleSubmit = async () => {
    const {
      createOrder,
      isEdit,
      updateOrder,
      order,
      clearBulkCustomerDetailsNewAction,
    } = props;
    if (isEdit) {
      if (isUpdateInProgress) return;

      setIsUpdateInProgress(true);

      const inputQtyMap: Map<string, number> = new Map(
        orderState.orderList.map((i) => [i.id as string, i.quantity])
      );

      const stock = await checkProductAvailabilityAPI(
        normalizeCheckProductPayload(
          orderState,
          zoneInfoRef.current,
          customerChannel
        )
      );

      const availableQtyMap: Map<string, number> = new Map(
        stock.map((i) => [i.id, i.availableStock])
      );

      const alreadyBooked: Map<string, number> = new Map(
        orderList.map((i) => [i.id as string, i.originalQuantity])
      );

      if (validateStock(inputQtyMap, availableQtyMap, alreadyBooked)) {
        handleProductAvailability(orderState);
        const normalizedUpdatePayload = normalizeUpdateOrderPayload(
          orderState.orderList,
          orderState.orderDiscount as number,
          taxList.current,
          showOrderDiscountModal,
          deliveryDate,
          orderType,
          orderState.partnerChannels || []
        );

        updateOrder(order.id?.toString() as string, normalizedUpdatePayload);
      }

      clearBulkCustomerDetailsNewAction();
    } else {
      // Check for product availability
      const inputQtyMap: Map<string, number> = new Map(
        orderList.map((i) => [i.id as string, i.quantity])
      );
      const stock = await checkProductAvailabilityAPI(
        normalizeCheckProductPayload(
          orderState,
          zoneInfoRef.current,
          customerChannel
        )
      );
      const availableQtyMap: Map<string, number> = new Map(
        stock.map((i) => [i.id, i.availableStock])
      );
      if (validateStock(inputQtyMap, availableQtyMap)) {
        handleProductAvailability(orderState);

        const normalizedOrderCreatePayload = normalizeCreateOrderPayload(
          orderState,
          taxList.current,
          showOrderDiscountModal,
          zoneInfoRef.current,
          useCredit ? selectedPlan : null,
          useCredit ? unFormattedGrandTotal : null
        );
        createOrder(normalizedOrderCreatePayload);
      }
    }
  };

  const getServiceCharge = (id: string) => {
    const plan = props.creditPaymentPlans.find((plan) => plan.id === id);
    return plan ? Number(plan.planCharge) : 0;
  };

  const checkAndReturnServiceCharge = () => {
    if (useCredit && props.creditPaymentPlans?.length) {
      if (selectedPlan) {
        creditPlanServiceCharge = getServiceCharge(selectedPlan);
        return creditPlanServiceCharge;
      }
    }
    return 0;
  };

  const calculateTotalAndDiscounts = () => {
    orderList &&
      orderList.map((item) => {
        totalDiscount +=
          (parseFloat(item.discount as any) +
            parseFloat(item.additionalDiscount as any)) *
          item.quantity;
        subTotal += (item.productOriginalPrice as number) * item.quantity;
        return null;
      });
    totalOrderLevelDiscount = orderState.orderDiscount as number;
    const grandTotalWithoutFormatting =
      checkAndReturnServiceCharge() +
      subTotal -
      (totalDiscount +
        Number(totalOrderLevelDiscount) +
        walletAmount +
        creditAmount);
    unFormattedGrandTotal = grandTotalWithoutFormatting;
    grandTotal = formatToCurrency(grandTotalWithoutFormatting);
    canUpdateWalletOrder =
      grandTotalWithoutFormatting + walletAmount >= walletAmount;
  };

  const renderOrderDetail = () => {
    return (
      <FormSectionLayout subTitleText="Customer details">
        {!isEdit && (
          <section className="form-group search-container">
            <label className="block text-uppercase label">
              Search for a user
            </label>
            <AsyncSelectComponent
              id="customerSearchSelect"
              name="customerSearchSelect"
              placeHolderText="Search by phone number or shop name"
              handleAsyncSelectChange={handleAsyncSelectChange}
              loadAsyncOptions={handleSearchInputChange}
              isCustomSelectValue={true}
              isDisabled={props.isEdit}
            />
          </section>
        )}

        <TextField
          id="storeDetails"
          label="Store details"
          placeholder="Store details"
          value={customerStore}
          disabled={true}
        />
        <TextField
          id="userNumber"
          label="Number"
          placeholder="User phone number"
          value={customerNumber}
          disabled={true}
        />
        <TextField
          id="customerChannel"
          label="Channel"
          placeholder="Customer channel"
          value={customerChannel}
          disabled={true}
        />
        {isCorporate || isSelfServiceStore ? (
          <TextField
            id="userCreditStatus"
            label="Credit Status"
            placeholder="User credit status"
            value={props.userCreditStatus.status}
            disabled={true}
          />
        ) : null}
      </FormSectionLayout>
    );
  };

  const renderDeliveryDate = () => {
    return (
      isEdit && (
        <div className="date-picker-container">
          Delivery Date is on{" "}
          <DatePicker
            selected={
              deliveryDate ? new Date(deliveryDate) : (deliveryDate as any)
            }
            dateFormat={DATE_FORMAT.ddMMyyyy}
            minDate={getTomorrowDate()}
            id="start-date"
            onChange={handleDeliveryDateChange}
            customInput={
              <span className="date-value">
                {" "}
                {deliveryDate
                  ? formatDate(deliveryDate.toString(), DATE_FORMAT.dd_MMM_yyyy)
                  : ""}
                <img src={CaretIcon} className="date-caret" alt="caret" />
              </span>
            }
          />
        </div>
      )
    );
  };

  const handleDeliveryDateChange = (date: Date | [Date, Date] | null) => {
    if (date) {
      //added 5 hours to date
      const newDate = addHours(date as Date, 0);
      setDeliveryDate(newDate.toString());
    }
  };

  const handleOrderTypeChange = (e: any) => {
    const fieldValue: string = e.target.value;
    setOrderType(fieldValue);
  };

  const handleValidateProducItems = (val: any) => {
    setValidateProductListVal(val);
  };

  const handleUseCreditChange = () => {
    const { creditPaymentPlans, userCreditPlan } = props;
    if (!userCreditPlan && creditPaymentPlans.length === 0) {
      props.fetchUserCreditLimit(orderState.customerId as string);
      props.fetchCreditPaymentPlans(
        orderState.customerChannel,
        orderState.customerId as string
      );
    }
    setUseCredit(!useCredit);
  };

  const handlePlanCheckboxChange = (id: string) => {
    setSelectedPlan(id);
  };

  const renderBasketDiscountBanner = () => {
    return (
      props.promosForOrder &&
      props.promosForOrder.BASKET &&
      props.promosForOrder?.BASKET.length > 0 && (
        <div className="basket-discount-banner">
          <p className="basket-discount-text">
            Rs. {props.promosForOrder.BASKET[0].discountSlab[0].discount} off on{" "}
            {
              (
                props.promosForOrder.BASKET[0]
                  .discountSlab[0] as IBasketPromoDiscountSlabs
              ).amount
            }{" "}
            ka order from{" "}
            {
              (props.promosForOrder.BASKET[0].payload as IBasketPromoMeta)
                .includedCategories[0].name
            }
          </p>
        </div>
      )
    );
  };

  const renderOrderListDetail = () => {
    return (
      <FormSectionLayout subTitleText="Order Details">
        {renderProductList()}
        <section className="search-container">
          {isEdit && (
            <div className="form-row">
              <Col>
                <div className="form-group">
                  <Label className="block text-uppercase label">
                    Order Type
                  </Label>
                  <select
                    className="form-control"
                    id="isDropshipOrder"
                    onChange={handleOrderTypeChange}
                    name="select"
                    value={orderType}
                  >
                    <option id="optionGO" value="GO">
                      General Order
                    </option>
                    <option id="optionDO" value="DO">
                      Dropship Order
                    </option>
                  </select>
                </div>
              </Col>
            </div>
          )}
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <InputGroupText className={!customerStore ? "disable-bg" : ""}>
                <img src="../../../images/no-search-icon.svg" alt="search" />
              </InputGroupText>
            </InputGroupAddon>
            <Input
              className="search-input"
              placeholder="Search by product name"
              onFocus={() => setIsShowModal(true)}
              disabled={!customerStore}
            />
          </InputGroup>
        </section>
        {renderBasketDiscountBanner()}
        <div className={`${customerStore.length < 1 && "hide"}`}>
          <OrderListComponent
            orderList={orderList}
            handleChange={handleOrderListChange}
            showDiscountModal={handleShowDiscountModal}
            unitsAvailable={unitsAvailable}
            handleStockValidationCheck={handleValidateProducItems}
            orderStatus={props.orderDetails.status}
          />
        </div>
      </FormSectionLayout>
    );
  };

  const renderSummaryDetail = () => {
    calculateTotalAndDiscounts();
    return (
      <FormSectionLayout subTitleText="Summary">
        <section className="summary-container">
          <Row className={customerStore.length ? "mb-2" : "d-none"}>
            <Col lg="9">Total</Col>
            <Col lg="3" className="text-right">
              Rs. {formatToCurrency(subTotal)}
            </Col>
          </Row>
          {totalDiscount > 0 && (
            <Row className={customerStore.length ? "mb-2" : "d-none"}>
              <Col lg="9">Item based discounts</Col>
              <Col lg="3" className="text-right">
                Rs.-{formatToCurrency(totalDiscount)}
              </Col>
            </Row>
          )}
          {totalOrderLevelDiscount > 0 ? (
            <Row className="mb-2">
              <Col lg="9">
                <span>Discount on full order</span>
                <span className="edit-order-level-discount">
                  <a
                    href="javascript:void(0)"
                    onClick={() => handleShowOrderDiscountModal(true)}
                  >
                    Edit...
                  </a>
                </span>
              </Col>
              <Col lg="3" className="text-right">
                Rs. -{formatToCurrency(Number(totalOrderLevelDiscount))}
              </Col>
            </Row>
          ) : (
            <Row className={customerStore.length ? "mb-2" : "d-none"}>
              <Col lg="7">
                <a
                  id="orderLevelDiscount"
                  href="javascript:void(0)"
                  onClick={() => handleShowOrderDiscountModal(true)}
                >
                  Discount on full order
                </a>
              </Col>
            </Row>
          )}
          {walletAmount > 0 && (
            <Row className="mb-2">
              <Col lg="9">Batwa</Col>
              <Col lg="3" className="text-right">
                Rs. -{formatToCurrency(walletAmount)}
              </Col>
            </Row>
          )}

          {creditAmount > 0 && (
            <Row className="mb-2">
              <Col lg="9">Credit</Col>
              <Col lg="3" className="text-right">
                Rs. -{formatToCurrency(creditAmount)}
              </Col>
            </Row>
          )}
          {useCredit && selectedPlan && (
            <Row className="mb-2">
              <Col lg="9">Service Charge</Col>
              <Col lg="3" className="text-right">
                Rs. {formatToCurrency(creditPlanServiceCharge)}
              </Col>
            </Row>
          )}
          <Row className="mb-2 bold">
            <div className="list-seperator"></div>
            <Col lg="9">Grand total</Col>
            <Col lg="3" className="text-right">
              Rs. {grandTotal}
            </Col>
          </Row>
        </section>
      </FormSectionLayout>
    );
  };

  const renderHorecaCredit = () => {
    return (
      <FormSectionLayout subTitleText="">
        <section className="summary-container">
          <Switch
            id="credit"
            label="Use Credit"
            checked={useCredit}
            handleSwitch={handleUseCreditChange}
            statusVal1={"ACTIVE"}
            statusVal2={"INACTIVE"}
            disabled={
              props.userCreditStatus.status !== CREDIT_USER_STATUS.ACTIVE
            }
          />
        </section>
      </FormSectionLayout>
    );
  };

  const renderPaymentTermsCheckbox = (
    creditPaymentPlans: ICreditPaymentPlansResponse[]
  ) => {
    return creditPaymentPlans.map((plan) => (
      <Checkbox
        key={plan.id}
        id={plan.id}
        label={plan.planName}
        handleCheckboxChange={() => handlePlanCheckboxChange(plan.id)}
        isChecked={selectedPlan === plan.id}
      />
    ));
  };

  const renderCreditLimitAndPaymentPlanOptions = () => {
    const { creditPaymentPlans, userCreditPlan } = props;
    // setCreditLimit(userCreditPlan?.availableLimit || 0)
    const availableLimit = userCreditPlan
      ? `Rs. ${formatToCurrency(userCreditPlan.availableLimit || 0)}`
      : `Not Available`;
    return (
      <FormSectionLayout subTitleText="Credit And Payment Plans">
        <section className="credit-plan-container">
          <Row className={"mb-2 available-credit"}>
            <Col lg="9">Available Credit</Col>
            <Col lg="3" className="text-right">
              {availableLimit}
            </Col>
          </Row>

          <Row className={"mb-2"}>
            <Col lg="9">Credit Payment Plans</Col>
            <Col lg="3" className="text-right plan-button-group">
              <ButtonGroup vertical>
                {creditPaymentPlans &&
                  creditPaymentPlans.length > 0 &&
                  renderPaymentTermsCheckbox(creditPaymentPlans)}
              </ButtonGroup>
            </Col>
          </Row>
        </section>
      </FormSectionLayout>
    );
  };

  const renderAddUpdateOrder = () => {
    return (
      <div id="addEditButtonRow">
        {props.isEdit &&
          props.orderDetails &&
          props.orderDetails.status === "OPEN" &&
          ability.can("update", "order") && (
            <ButtonComponent
              buttonText={buttonText}
              onClickHandler={isUpdateOrder}
              isDisable={
                validateProductListVal.validateForm === "disabled" ||
                !canUpdateWalletOrder ||
                zoneInfoRef.current.zoneStatus !== "ACTIVE" ||
                (creditAmount > 0 && !allowedRole("Commando God")) ||
                isOrderEditBlocked ||
                bundleProductExist ||
                (props.userCreditPlan
                  ? unFormattedGrandTotal > props.userCreditPlan.availableLimit
                  : false)
              }
            />
          )}
        {!props.isEdit && (
          <ButtonComponent
            buttonText={buttonText}
            onClickHandler={isCreateOrder}
            isDisable={
              validateProductListVal.validateForm === "disabled" ||
              customerStore.length < 1 ||
              zoneInfoRef.current.zoneStatus !== "ACTIVE" ||
              (props.userCreditPlan
                ? unFormattedGrandTotal > props.userCreditPlan.availableLimit
                : false)
            }
          />
        )}
        {props.isEdit &&
          props.orderDetails &&
          props.orderDetails.status === "OPEN" &&
          ability.can("update", "order") && (
            <DropdownButton
              classNames="dropdown more-actions"
              dropDownButtonText="More actions"
              dropdownItem={list}
            />
          )}
      </div>
    );
  };

  const renderSearchProductModalBody = () => {
    const buttonText =
      selectedProducts.current.length > 0
        ? `Update order (${selectedProducts.current.length} items selected)`
        : `Update order`;
    return (
      <>
        <InputGroup>
          <InputGroupAddon addonType="prepend">
            <InputGroupText>
              <img src="../../../images/no-search-icon.svg" alt="search" />
            </InputGroupText>
          </InputGroupAddon>
          <Input
            className="search-input"
            placeholder="Search by product name"
            onChange={handleProductSearchInputChange}
            value={searchTerm}
            autoFocus={true}
          />
          <div
            className={isSearch ? "close-button" : "hide close-button"}
            onClick={closeSearch}
          >
            <img src="../../../images/close.svg" alt="close" />
          </div>
        </InputGroup>
        <section className="product-list-container">
          {productData.length > 0 && (
            <DataTableProductSearch
              tableId="product-list"
              tableHeaderData={null}
              tableFooterData={footerData}
              tableBodyDataTypes={bodyDataAndKeyTypes}
              tableBodyRowData={productData}
              noSearchResultFlag={isSearch}
              editRowHandler={handleRecordSelection}
            />
          )}
          {isProductLoading && (
            <div>
              <Spinner color="primary" className="spinner" />
            </div>
          )}
        </section>
        <div className="update-order-container">
          <ButtonComponent
            buttonClass="large-button btn-primary"
            buttonText={buttonText}
            onClickHandler={updateOrderList}
          />
        </div>
      </>
    );
  };

  const renderOrderLevelDiscountModalBody = () => {
    return (
      <>
        <TextField
          id="order-discount"
          value={showOrderDiscountModal.value}
          label="Value"
          placeholder="e.g. 23"
          handleChange={handleDiscountChange}
          error={decimalOnlyValidator(showOrderDiscountModal.value.toString())}
          disabled={showOrderDiscountModal.isBasketDiscount}
        />
        <SelectDropDown
          label="Reason"
          options={[]}
          id="orderLevelDiscountReason"
          handleChange={() => {}}
          value={1}
          defaultOption={{
            id: "-1",
            value: "",
            text: "",
          }}
        />
        <div className="update-order-container order-level-discount">
          <ButtonComponent
            buttonClass="large-button btn-primary"
            buttonText="Apply"
            onClickHandler={() => {
              applyDiscount(
                true,
                {
                  isBasketDiscount: showOrderDiscountModal.isBasketDiscount,
                  promoId: showOrderDiscountModal.promoId,
                  show: showOrderDiscountModal.show,
                  promoType: showOrderDiscountModal.promoType,
                  value: showOrderDiscountModal.value,
                },
                true
              );
              Number(showOrderDiscountModal.value) === 0 &&
                validateAndCallApplyBasketPromoMethod();
            }}
            isDisable={showOrderDiscountModal.isBasketDiscount}
          />
        </div>
      </>
    );
  };

  const renderDiscountModalBody = () => {
    return (
      <>
        <TextField
          id="discount"
          value={showDiscountModal.value}
          label="Discount"
          placeholder="e.g. 23"
          disabled
          error={decimalOnlyValidator(showDiscountModal.value.toString())}
        />
        <TextField
          id="additional-discount"
          value={showDiscountModal.additionalDiscount}
          label="Additional Discount"
          placeholder="e.g. 23"
          handleChange={handleDiscountChange}
          error={decimalOnlyValidator(
            showDiscountModal.additionalDiscount.toString()
          )}
        />
        <SelectDropDown
          label="Reason"
          options={[]}
          id="prductDiscountReason"
          handleChange={() => {}}
          value={1}
          defaultOption={{
            id: "-1",
            value: "",
            text: "",
          }}
        />
        <div className="update-order-container">
          <ButtonComponent
            buttonClass="large-button btn-primary"
            buttonText="Apply"
            onClickHandler={() => applyDiscount(false)}
          />
        </div>
      </>
    );
  };

  const renderProductList = () => {
    return (
      <Modal
        showModal={showModal}
        modalHeading="Product search"
        closeModalHandler={closeModal}
        showProgressString={false}
        failedRecords=""
        modalBodyClass="product-search"
      >
        {renderSearchProductModalBody()}
      </Modal>
    );
  };

  const renderOrderItemLevelDiscountModal = () => {
    return (
      <Modal
        showModal={showDiscountModal.show}
        modalHeading="Discount on item"
        closeModalHandler={() =>
          setIsShowDiscountModal({
            value: showDiscountModal.value,
            additionalDiscount: showDiscountModal.additionalDiscount,
            show: false,
            selectedRow: showDiscountModal.selectedRow,
          })
        }
        showProgressString={false}
        failedRecords=""
        modalBodyClass="discount-modal"
        childrenWrapper="children-modal"
      >
        {renderDiscountModalBody()}
      </Modal>
    );
  };

  const renderOrderLevelDiscountModal = () => {
    return (
      <Modal
        showModal={showOrderDiscountModal.show}
        modalHeading="Discount on full order"
        closeModalHandler={() =>
          setIsShowOrderDiscountModal({
            value: showOrderDiscountModal.value,
            show: false,
            isBasketDiscount: showOrderDiscountModal.isBasketDiscount,
            promoId: showOrderDiscountModal.promoId,
            promoType: showOrderDiscountModal.promoType,
          })
        }
        showProgressString={false}
        failedRecords=""
        modalBodyClass="discount-modal"
        childrenWrapper="children-modal"
      >
        {renderOrderLevelDiscountModalBody()}
      </Modal>
    );
  };

  const renderNotificationModal = () => {
    return (
      <Modal
        showModal={notificationModal}
        closeModalHandler={notificationModalState}
        modalHeading="Are you sure?"
        showProgressString={false}
        failedRecords=""
        modalBodyClass="notification-account"
        containerClass="notification-modal"
      >
        <div id="confirmationModal">
          <p>
            Are you sure you want to
            {updateOrder && <span> update </span>}
            {createOrder && <span> create </span>}
            this order?
          </p>
          <Row className="button-container">
            <Col lg="6">
              <ButtonComponent
                buttonClass="button-small btn btn-primary-outline"
                buttonText="Go back"
                onClickHandler={notificationModalState}
              />
            </Col>
            <Col lg="6">
              {updateOrder && (
                <ButtonComponent
                  buttonClass="button-small btn btn-primary verify-btn"
                  buttonText="Update"
                  onClickHandler={handleSubmit}
                  isDisable={isUpdateInProgress}
                />
              )}
              {createOrder && (
                <ButtonComponent
                  buttonClass="button-small btn btn-primary verify-btn"
                  buttonText="Create order"
                  onClickHandler={handleSubmit}
                />
              )}
            </Col>
          </Row>
        </div>
      </Modal>
    );
  };

  const reopenItemsModal = () => {
    showMinimumOrderValueModal.current = false;
    setNotificationModal(false);
    setCreateOrder(false);
    setIsShowModal(!showModal);
  };

  return (
    <Container fluid>
      {showMinimumOrderValueModal.current && (
        <Modal
          showModal={showMinimumOrderValueModal.current}
          modalHeading="Minimum order value"
          closeModalHandler={closeModal}
          showProgressString={false}
          failedRecords=""
          modalBodyClass="minimum-order-value"
          containerClass="error-modal"
        >
          <section className="content-wrapper">
            <img src={icnminimumordervalue} />
            <section className="minimum-order-value-message">
              Sorry, you can not place the order and need an additional Rs.{" "}
              {error && (error! as any).errorValue}{" "}
              {!error &&
                minimumOrderValueResult.current.minOrderValue &&
                minimumOrderValueResult.current.minOrderValue}{" "}
              to proceed with the order.
            </section>
            <section className="add-more-items">
              <ButtonComponent
                buttonText="Add more items"
                buttonClass="btn-primary btn btn-secondary button-full"
                onClickHandler={reopenItemsModal}
              />
            </section>
          </section>
        </Modal>
      )}
      {(props.isLoading || isLoading) && <LoaderComponent />}
      {renderNotificationModal()}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={"toaster toaster-bg-" + showToaster.type}
      >
        {showToaster.message || ""}
      </Toaster>
      <Row>
        <Col lg="5">
          <div className="back-button">
            <a href="javascript:void(0)" onClick={handleGoBackButton}>
              {" "}
              {/* TO-DO: Convert this to a button in all compoents. */}
              <span>
                <img src={"../../../images/back-arrow.svg"} alt="close" />
              </span>{" "}
              Back to Orders
            </a>
          </div>
          <HeadingText text={headingText} isMainHeading={true} />
        </Col>
        <Col lg="5">
          {props.isEdit && (
            <section className="order-status-container">
              <section className="main-status-wrapper">
                <section className="order-status-wrapper">
                  <span className="order-current-status">
                    {props.orderDetails.status}
                  </span>
                  <span className="bullet"></span>
                  <span className="order-isfulfilled">
                    {props.orderDetails.fulfillmentStatus}
                  </span>{" "}
                  {props.orderDetails && props.orderDetails.returnStatus && (
                    <span>
                      <span className="bullet"></span>
                      <span className="order-isreturn">
                        {props.orderDetails.returnStatus}
                      </span>
                    </span>
                  )}
                </section>
                <section className="order-locked-wrapper">
                  <span className="order-current-status">
                    {isOrderEditBlocked ? "LOCKED" : ""}
                  </span>
                </section>
              </section>
              <div className="clearfix"></div>
              {renderDeliveryDate()}
              {props.orderDetails && (
                <LoggingViewer
                  createdAt={createdAt}
                  createdBy={createdBy}
                  updatedAt={updatedAt}
                  updatedBy={updatedBy}
                />
              )}
              <button
                className="download-btn"
                data-testid="invoice-generate-button"
                onClick={() =>
                  generatePDFHandler(orderList, orderState, creditAmount, cash)
                }
              >
                <span className="download-button-span">
                  <img
                    data-testid="download-button"
                    className="download-icon"
                    src={DownloadButton}
                    alt="download-button"
                  />
                  Generate Invoice
                </span>
              </button>
            </section>
          )}
        </Col>
      </Row>
      <Row className="form-container">
        <Col xs="12">
          <Form>
            <div className="order-detail">{renderOrderDetail()}</div>
            <div className={`order-list-detail form-row-separator`}>
              {renderOrderListDetail()}
            </div>
            <div className="summary-detail form-row-separator">
              {renderSummaryDetail()}
            </div>
            {orderState &&
              orderState.customerId &&
              (isCorporate ||
                // (orderState.customerChannel === "CORPORATE" ||
                orderState.customerChannel === "SELF_SERVICE_STORE") &&
              !isEdit && (
                <div className="summary-detail form-row-separator">
                  {renderHorecaCredit()}
                </div>
              )}
            {useCredit && renderCreditLimitAndPaymentPlanOptions()}
            <div className="summary-detail form-row-separator">
              <Row className="mb-2">
                <Col lg="4"></Col>
                <Col lg="8" className="text-left">
                  {renderAddUpdateOrder()}
                </Col>
              </Row>
            </div>
          </Form>
        </Col>
      </Row>
      {renderOrderItemLevelDiscountModal()}
      {renderOrderLevelDiscountModal()}
    </Container>
  );
};

export default AddUpdateOrderPage;
